import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  effect,
  inject,
  input,
  model,
  viewChild
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  CachedBuildingService,
  DeleteLocalCacheCategory,
  ENVIRONMENT,
  LazyModuleInjector,
  ModalService,
  PageSectionModule,
  SharedService,
  TokenService,
  UserService
} from '@cfp/core';
import { AuditTrailModal } from '@cfp/features-shared/audit-trail';
import { EnergyLabelRequirementChangeOption } from '@cfp/features-shared/energy-label-requirement';
import { NotesOption } from '@cfp/features-shared/notes';
import { TagsBuildingModal } from '@cfp/features-shared/tags';
import { BuildingAddress } from '@cfp/shared/building/address-module';
import { LabelGroupService } from '@cfp/shared/ui/label-group';
import { PdfService } from '@cfp/shared/ui/pdf';
import { CfpTranslateModule, Dropdown, Icon, OrderByModule } from '@cfp/ui';
import { AlertModalResult, AlertService } from '@cfp/ui/feedback';
import {
  BuildingChangeLogResponseDTO,
  BuildingChangeLogService,
  BuildingDetailsDTO,
  BuildingsService,
  CountryCodeDTO
} from 'csr-asset-app';
import { LicenseDTO } from 'csr-login-app';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { combineLatest, forkJoin, map } from 'rxjs';
import { BuildingCodeModal } from '../code-modal';
import { BuildingDisplayAddressChangeOptionComponent } from '../display-address';
import { BuildingTags } from '../tags';
import { AddressSelectedEvent, SelectBuildingCombobox } from './select-building-combobox';

interface AllowedModule {
  module: string;
  backPath: string;
}

@Component({
  selector: 'cfp-building-header',
  templateUrl: './building-header.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BuildingAddress,
    Dropdown,
    Icon,
    RouterModule,
    CfpTranslateModule,
    CommonModule,
    OrderByModule,
    FormsModule,
    SelectBuildingCombobox,
    EnergyLabelRequirementChangeOption,
    BuildingDisplayAddressChangeOptionComponent,
    NotesOption,
    PageSectionModule,
    BuildingTags
  ]
})
export class BuildingHeader implements OnInit {
  readonly selectBuildingCombobox = viewChild<SelectBuildingCombobox>('selectBuildingCombobox');

  public readonly address = model.required<string[]>();
  public readonly countryCode = input<CountryCodeDTO>();
  public readonly showMonumentIcon = input<boolean>(false);
  public readonly name = model<string>();
  public readonly buildingId = input<number>();
  public readonly portfolioId = input<number>();
  public readonly buildingCode = input<string>();
  public readonly combineNameAndAddress = input<boolean>(false);
  public readonly hideSelect = input<boolean>(false);
  public readonly hideDropdown = input<boolean>(false);
  public readonly hideGoToAllBuildings = input<boolean>(false);
  public readonly hideDeleteBuilding = input<boolean>(false);
  public readonly hideManageBuildingTags = input<boolean>(false);
  public readonly hideAuditTrail = input<boolean>(false);
  public readonly canDownloadReportFromInput = input<boolean>(false, { alias: 'canDownloadReport' });
  public readonly buildingHeaderClass = input<string>('col-12 ms-lg-auto col-lg-auto mt-3 mt-lg-0');
  public readonly customBuildingImageUrl = input<string>();
  public readonly useH1 = input<boolean>(true);

  protected copied: boolean = false;
  protected loading: boolean = false;
  protected backPath: string;
  protected showBuildingName: boolean = false;
  protected showBuildingCode: boolean = true;

  protected limit: number = 10;
  protected offset: number = 0;

  private readonly allowedModules: AllowedModule[] = [
    { module: 'energy', backPath: '/${module}/${portfolioId}/buildings' },
    { module: 'roadmap', backPath: '/${module}/${portfolioId}' },
    { module: 'breeam', backPath: '/${module}/${portfolioId}/buildings' },
    { module: 'climate-risk', backPath: '/${module}/${portfolioId}/buildings' }
  ];

  private readonly environment = inject(ENVIRONMENT);
  private readonly labelGroupService = inject(LabelGroupService);
  private readonly alertService = inject(AlertService);
  private readonly buildingsService = inject(BuildingsService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly tokenService = inject(TokenService);
  private readonly pdfService = inject(PdfService);
  private readonly modalService = inject(ModalService);
  private readonly userService = inject(UserService);
  private readonly sharedService = inject(SharedService);
  private readonly buildingChangeLogService = inject(BuildingChangeLogService);
  private readonly cachedBuildingService = inject(CachedBuildingService);
  private readonly lazyModuleInjector = inject(LazyModuleInjector);

  constructor() {
    this.showBuildingName = this.environment.components?.showBuildingName === true;

    effect(() => {
      const portfolioId = this.portfolioId();
      if (!portfolioId) {
        return;
      }
      this.initBackPath(portfolioId);
    });
  }

  protected readonly canDeleteBuilding = computed(
    () => this.tokenService.hasScope('buildings-delete') && !this.hideDeleteBuilding()
  );
  protected readonly canModifyBuildingProperties = computed(
    () => this.tokenService.hasScope('buildings-properties-modify') && !this.hideDeleteBuilding()
  );
  protected readonly hasAuditTrailLicense = toSignal(this.userService.hasLicense('building_change_log'));
  protected readonly hasEnergyLabelRequirementLicense = toSignal(
    this.userService.hasLicense('energy_label_requirement')
  );
  protected readonly canDownloadReport = toSignal(
    combineLatest([
      this.userService.hasLicense(LicenseDTO.LicenseTypeEnum.BuildingReport),
      toObservable(this.canDownloadReportFromInput)
    ]).pipe(map(([hasLicense, canDownloadReport]) => hasLicense && canDownloadReport))
  );

  public ngOnInit(): void {
    this.userService
      .hasAnyRole(['ROLE_RELATION_MANAGER', 'ROLE_RELATION_MANAGER_VIEWER'])
      .subscribe((hasRole: boolean) => {
        this.showBuildingCode = hasRole && this.environment.components?.showBuildingCode === true;
      });

    this.activatedRoute.url.subscribe(() => {
      const portfolioId = this.portfolioId();
      if (!portfolioId) {
        return;
      }
      this.initBackPath(portfolioId);
    });
  }

  protected hasSearchValue(): boolean {
    return !!this.selectBuildingCombobox()?.searchValue;
  }

  protected changeBuildingCode(): void {
    this.modalService.show(BuildingCodeModal, {
      class: 'modal-sm',
      initialState: {
        buildingId: this.buildingId()
      }
    });
  }

  protected addressSelected(event: AddressSelectedEvent): void {
    this.address.set(event.address);
    this.name.set(event.name);
  }

  protected downloadReport(): void {
    const labelGroup = this.labelGroupService.getStoredPreferredLabelGroup();
    this.pdfService.download(
      `/report/${this.portfolioId()}/buildings/${this.buildingId()}`,
      this.getFileName(),
      labelGroup
    );
  }

  protected deleteBuilding(): void {
    const buildingId = this.buildingId();
    if (!buildingId) {
      return;
    }
    this.alertService
      .showConfirmation({
        type: 'warning',
        title: 'building.remove.title',
        body: 'building.remove.text',
        confirmText: 'remove.button',
        cancelText: 'cancel.button'
      })
      .subscribe({
        next: (result: AlertModalResult) => {
          if (result !== AlertModalResult.CONFIRM) {
            return;
          }

          this.loading = true;
          this.buildingsService.v2BuildingsIdRemovePost(buildingId).subscribe({
            next: () => {
              this.sharedService.emitDeleteLocalCache(DeleteLocalCacheCategory.Portfolio);
              this.alertService.showAlert('success', 'buildingRemoved').subscribe(() => {
                this.router.navigateByUrl(this.backPath);
              });
            },
            error: () => {
              this.alertService.showAlert('error', 'building.remove.error');
              this.loading = false;
            }
          });
        }
      });
  }

  protected showAuditTrail(): void {
    const buildingId = this.buildingId();
    if (!buildingId) {
      return;
    }
    let clientTimeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;
    forkJoin([
      this.cachedBuildingService.v2BuildingsIdGet(buildingId),
      this.buildingChangeLogService.v2BuildingsIdChangeLogsGet(buildingId, clientTimeZone, this.limit, this.offset)
    ]).subscribe(([building, buildingChangeLogResponse]: [BuildingDetailsDTO, BuildingChangeLogResponseDTO]) => {
      const auditTrailModal: BsModalRef<AuditTrailModal> = this.modalService.showSidePanel(AuditTrailModal, {
        initialState: {
          buildingChangeLogItems: buildingChangeLogResponse.changes,
          offset: buildingChangeLogResponse.paging.offset,
          total: buildingChangeLogResponse.paging.total,
          building: building
        }
      });

      const modalContent = auditTrailModal.content;
      if (modalContent) {
        modalContent.setOffsetLimit.subscribe((offsetLimit: { limit: number; offset: number }) => {
          this.buildingChangeLogService
            .v2BuildingsIdChangeLogsGet(buildingId, clientTimeZone, offsetLimit.limit, offsetLimit.offset)
            .subscribe((buildingChangeLogResponse: BuildingChangeLogResponseDTO) => {
              modalContent.offset = buildingChangeLogResponse.paging.offset;
              modalContent.buildingChangeLogItems = buildingChangeLogResponse.changes;
            });
        });
      }
    });
  }

  protected copyBuildingAddress(): void {
    const textarea = document.createElement('textarea');
    textarea.style.position = 'fixed';
    textarea.style.left = '0';
    textarea.style.top = '0';
    textarea.style.opacity = '0';
    textarea.value = this.address().join(' ');
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    this.copied = true;
    setTimeout(() => {
      this.copied = false;
    }, 500);
  }

  private getFileName(): string {
    return this.address().join('-').replace(/,/g, '').replace(/ /g, '-');
  }

  private initBackPath(portfolioId: number): void {
    const module = this.router.url.split('/')[1];
    let allowedModule = this.allowedModules.find((allowedModule) => allowedModule.module === module);
    if (!allowedModule) {
      allowedModule = this.allowedModules[0];
    }
    this.backPath = allowedModule.backPath
      .replace('${module}', allowedModule.module)
      .replace('${portfolioId}', portfolioId.toString());
  }

  protected showTags(): void {
    this.modalService.showSidePanel(TagsBuildingModal, {
      initialState: {
        portfolioId: this.portfolioId(),
        buildingId: this.buildingId()
      }
    });
  }
}
